import { Minus, TrendingDown, TrendingUp } from 'lucide-react';
import { Stage, type Team } from 'modules/client/tournaments/domain/types';
import getFormIcon from 'modules/client/tournaments/domain/utils/get-form-icon';
import getRoundTypeLabel from 'modules/client/tournaments/domain/utils/get-round-type-label';
import React from 'react';

interface StageStandingsTableProps {
  stage: Stage;
  teams: Team[];
}

const StageStandingsTable: React.FC<StageStandingsTableProps> = ({ stage, teams }) => {
  const getPositionTrend = (position: number) => {
    if (position <= 3) {
      return <TrendingUp className="text-emerald-600" size={16} />;
    }
    if (position >= 6) {
      return <TrendingDown className="text-red-600" size={16} />;
    }
    return <Minus className="text-gray-400" size={16} />;
  };

  const getTeamInfo = (teamId: number) => {
    return teams.find((t) => t.id === teamId);
  };

  const sortedTeams = [...stage.teams].sort((a, b) => {
    return a.position - b.position;
  });

  return (
    <div className="overflow-hidden rounded-lg bg-white shadow-md">
      <div className="bg-emerald-600 px-6 py-4 text-white">
        <h2 className="text-xl font-bold">{stage.name}</h2>
        <p className="text-emerald-100">
          {getRoundTypeLabel(stage.type)} • {stage.teams.length} команд
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">#</th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Команда</th>
              <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">И</th>
              <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">В</th>
              <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">Н</th>
              <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">П</th>
              <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">ЗМ</th>
              <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">ПМ</th>
              <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">РМ</th>
              <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">О</th>
              <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">Форма</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {sortedTeams.map((stageTeam, index) => {
              const teamInfo = getTeamInfo(stageTeam.teamId);
              if (!teamInfo) {
                return null;
              }

              return (
                <tr
                  key={stageTeam.teamId}
                  className={`transition-colors hover:bg-gray-50 ${
                    index < 3 ? 'bg-emerald-50' : index >= sortedTeams.length - 2 ? 'bg-red-50' : ''
                  }`}
                >
                  <td className="whitespace-nowrap px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <span
                        className={`text-sm font-medium ${
                          index < 3 ? 'text-emerald-600' : index >= sortedTeams.length - 2 ? 'text-red-600' : 'text-gray-900'
                        }`}
                      >
                        {index + 1}
                      </span>
                      {getPositionTrend(index + 1)}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <span className="text-xl">
                        <img src={teamInfo.logo} alt="" className="size-10 rounded-lg" />
                      </span>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{teamInfo.name}</div>
                        {teamInfo.shortName !== teamInfo.name && <div className="text-sm text-gray-500">{teamInfo.shortName}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-center text-sm text-gray-900">{stageTeam.played}</td>
                  <td className="whitespace-nowrap px-6 py-4 text-center text-sm text-gray-900">{stageTeam.won}</td>
                  <td className="whitespace-nowrap px-6 py-4 text-center text-sm text-gray-900">{stageTeam.drawn}</td>
                  <td className="whitespace-nowrap px-6 py-4 text-center text-sm text-gray-900">{stageTeam.lost}</td>
                  <td className="whitespace-nowrap px-6 py-4 text-center text-sm text-gray-900">{stageTeam.goalsFor}</td>
                  <td className="whitespace-nowrap px-6 py-4 text-center text-sm text-gray-900">{stageTeam.goalsAgainst}</td>
                  <td
                    className={`whitespace-nowrap px-6 py-4 text-center text-sm font-medium ${
                      stageTeam.goalDifference > 0
                        ? 'text-emerald-600'
                        : stageTeam.goalDifference < 0
                          ? 'text-red-600'
                          : 'text-gray-900'
                    }`}
                  >
                    {stageTeam.goalDifference > 0 ? '+' : ''}
                    {stageTeam.goalDifference}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-center text-sm font-bold text-gray-900">{stageTeam.points}</td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <div className="flex justify-center space-x-1">
                      {stageTeam.form.map((result, idx) => (
                        <div key={idx}>{getFormIcon(result)}</div>
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="bg-gray-50 px-6 py-4 text-xs text-gray-600">
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center space-x-2">
            <div className="h-3 w-3 rounded bg-emerald-600"></div>
            <span>Лидирующие позиции</span>
          </div>
          {stage.isPlayoff && (
            <div className="flex items-center space-x-2">
              <div className="h-3 w-3 rounded bg-yellow-500"></div>
              <span>Плей-офф</span>
            </div>
          )}
          <div className="flex items-center space-x-2">
            <div className="h-3 w-3 rounded bg-red-600"></div>
            <span>Нижние позиции</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StageStandingsTable;
